import { useEffect, useLayoutEffect, useMemo } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { AppShell } from "@/app/AppShell";
import {
  APP_ROUTE_DEFINITIONS,
  SAFE_TRACKING_QUERY_PARAM_KEYS,
  applySafeTrackingQueryParams,
  extractSafeTrackingQueryParams,
  type SafeTrackingQueryParams
} from "@/features/navigation/appRouteManifest";

function readTopLevelSearchParams(): URLSearchParams {
  if (typeof window === "undefined") {
    return new URLSearchParams();
  }

  return new URLSearchParams(window.location.search);
}

function normalizeSearch(searchParams: URLSearchParams): string {
  const normalizedSearch = searchParams.toString();
  return normalizedSearch.length > 0 ? `?${normalizedSearch}` : "";
}

function syncTopLevelSearchWithSafeTrackingParams(
  trackingQueryParams: SafeTrackingQueryParams
): void {
  if (typeof window === "undefined") {
    return;
  }

  const topLevelSearchParams = new URLSearchParams(window.location.search);
  for (const trackingQueryParamKey of SAFE_TRACKING_QUERY_PARAM_KEYS) {
    topLevelSearchParams.delete(trackingQueryParamKey);
  }
  applySafeTrackingQueryParams(topLevelSearchParams, trackingQueryParams);

  const nextTopLevelSearch = normalizeSearch(topLevelSearchParams);
  if (nextTopLevelSearch === window.location.search) {
    return;
  }

  const nextTopLevelUrl = `${window.location.pathname}${nextTopLevelSearch}${window.location.hash}`;
  window.history.replaceState(window.history.state, "", nextTopLevelUrl);
}

export function AppShellRoute() {
  const location = useLocation();
  const { t } = useTranslation();
  const routeSearchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const trackingQueryParams = useMemo(
    () => extractSafeTrackingQueryParams(routeSearchParams, readTopLevelSearchParams()),
    [routeSearchParams]
  );

  useLayoutEffect(() => {
    syncTopLevelSearchWithSafeTrackingParams(trackingQueryParams);
  }, [trackingQueryParams]);

  // Home and routes without a nav label use the bare app title. react-i18next
  // hands out a new `t` when the language changes, so `t` also tracks locale.
  useEffect(() => {
    const activeRoute = APP_ROUTE_DEFINITIONS.find((route) => route.href === location.pathname);
    const appTitle = t("app.title");
    document.title =
      activeRoute && activeRoute.pathSegment !== ""
        ? `${t(activeRoute.navLabelKey)} · ${appTitle}`
        : appTitle;
  }, [location.pathname, t]);

  const canonicalRouteSearchParams = new URLSearchParams(routeSearchParams);
  for (const trackingQueryParamKey of SAFE_TRACKING_QUERY_PARAM_KEYS) {
    canonicalRouteSearchParams.delete(trackingQueryParamKey);
  }
  const canonicalRouteSearch = normalizeSearch(canonicalRouteSearchParams);

  if (canonicalRouteSearch !== location.search) {
    return <Navigate to={`${location.pathname}${canonicalRouteSearch}`} replace />;
  }

  return <AppShell />;
}
